import { ID_SEGMENT_PATTERNS, QUERY_PARAM_ALLOWLIST } from './spec';

/**
 * Replace id-shaped path segments with `:id`.
 *
 * Firmable URLs are `/dashboard/company/<uuid>` and
 * `/internal/people/<id>`, so the raw path is both high-cardinality and
 * customer-identifying. The template is what you actually want to group by.
 */
export function templatizePath(pathname: string): string {
  return templatizeParts(pathname).path;
}

/**
 * Templatize, and report what was replaced.
 *
 * The ids have to come back out somewhere. Templating keeps `span_name` and
 * `url.full` low-cardinality — which is why it exists — but if that is the only
 * thing that happens to them, a trace can tell you a company was fetched and
 * never which company. Callers put these on a separate, non-indexed attribute.
 */
export function templatizeParts(pathname: string): { path: string; ids: string[] } {
  const ids: string[] = [];
  const path = pathname
    .split('/')
    .map((segment) => {
      if (segment && ID_SEGMENT_PATTERNS.some((re) => re.test(segment))) {
        ids.push(segment);
        return ':id';
      }
      return segment;
    })
    .join('/');
  return { path, ids };
}

export interface SanitizedUrl {
  /** origin + templated path + allow-listed query params. Fragment always dropped. */
  url: string;
  /** Sorted names of the query params that were present, values discarded. */
  queryKeys: string;
  /** The id-shaped path segments that were replaced by `:id`, in order. */
  pathIds: string;
}

/**
 * Reduce a URL to something safe to index. Search terms live in query strings,
 * so anything not explicitly allow-listed is dropped rather than scrubbed.
 */
export function sanitizeUrlParts(raw: string): SanitizedUrl {
  let parsed: URL;
  try {
    parsed = new URL(raw, 'https://fbl.invalid');
  } catch {
    return { url: '[fbl-redacted:unparseable-url]', queryKeys: '', pathIds: '' };
  }

  const kept = new URLSearchParams();
  const names: string[] = [];
  parsed.searchParams.forEach((value, key) => {
    names.push(key);
    if (QUERY_PARAM_ALLOWLIST.has(key.toLowerCase())) kept.append(key, value);
  });

  const origin = parsed.origin === 'https://fbl.invalid' ? '' : parsed.origin;
  const query = kept.toString();
  const { path, ids } = templatizeParts(parsed.pathname);

  return {
    url: `${origin}${path}${query ? `?${query}` : ''}`,
    queryKeys: Array.from(new Set(names)).sort().join(','),
    pathIds: ids.join(',').slice(0, 200),
  };
}

export const sanitizeUrl = (raw: string): string => sanitizeUrlParts(raw).url;
